import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Check,
  CircleDot,
  Combine,
  FileAudio,
  FileVideo,
  Loader2,
  RotateCcw,
  Square,
  TriangleAlert,
  Upload,
} from "lucide-react";
import { MAX_UPLOAD_BYTES } from "@protocol";
import type { Id } from "@convex/_generated/dataModel";
import { formatElapsed, primaryButtonClass, secondaryButtonClass } from "@/lib/ui";
import { isBusy, useRecorder } from "@/recorder/context";
import { AUDIO_ACCEPT, formatBytes } from "@/recorder/audioFile";
import { useFileDropTarget } from "@/recorder/dropTarget";
import ConnectionChip from "./ConnectionChip";
import RecordSplitButton from "./RecordSplitButton";
import { ProgressBar } from "./UploadProgress";

const enter = {
  initial: { opacity: 0, y: 3 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -3 },
  transition: { duration: 0.18, ease: "easeOut" },
} as const;

/**
 * Record controls for one lesson, rendered inside the calendar's lesson dialog.
 *
 * There is a single microphone, so the panel is explicit about a recording
 * belonging to a *different* lesson rather than offering a second Record button
 * that would fail. An audio file can stand in for a live capture: picked from
 * the Record menu or the drop zone here, or dropped anywhere while this dialog
 * is open - it registers itself as the drop target.
 */
export default function RecorderPanel({
  lessonId,
  lessonLabel,
}: {
  lessonId: string;
  lessonLabel: string;
}) {
  const recorder = useRecorder();
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  useFileDropTarget(lessonId as Id<"lessons">, lessonLabel);

  const isThisLesson = recorder.lessonId === lessonId;
  const isUpload = recorder.mode === "upload";
  const capturing = recorder.status === "recording" && isThisLesson;
  const draining =
    (recorder.status === "uploading" || recorder.status === "finalizing") && isThisLesson;
  const isStored = recorder.status === "done" && isThisLesson;
  const elsewhere = !isThisLesson && isBusy(recorder.status);
  const starting = recorder.status === "requesting";
  const joining = recorder.upload?.phase === "joining";
  const extracting =
    starting &&
    isUpload &&
    (recorder.upload?.phase === "extracting" || joining) &&
    isThisLesson;

  async function guard(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  }

  const record = () =>
    void guard(async () => {
      if (recorder.status === "done" || recorder.status === "error") {
        recorder.reset();
      }
      await recorder.start(lessonId as Id<"lessons">);
    });

  const uploadFiles = (files: File[]) =>
    void guard(() => recorder.uploadFiles(files, lessonId as Id<"lessons">));

  const currentMode = capturing
    ? "capturing"
    : extracting
      ? "extracting"
      : draining
      ? isUpload
        ? "uploading-file"
        : "draining"
      : isStored
        ? isUpload
          ? "uploaded"
          : "stored"
        : "idle";

  const progress = recorder.upload?.progress ?? 0;
  const finalizing = recorder.status === "finalizing" || progress >= 1;

  return (
    <motion.div
      layout
      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="flex flex-col gap-2 rounded-lg border border-[#d1d1cf] p-3 dark:border-[#2a2a28]"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-[11px] uppercase tracking-wider text-neutral-500">
          Recorder
        </span>
        <AnimatePresence mode="wait">
          {(capturing || draining) && (
            <motion.div
              key="connected"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <ConnectionChip status={recorder.connection} />
            </motion.div>
          )}
          {isStored && (
            <motion.span
              key="stored-chip"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="font-mono text-[10px] text-[#22C55E]"
            >
              {isUpload ? "uploaded" : "stored"}
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      {elsewhere && (
        <p className="text-xs text-neutral-500">
          {isUpload
            ? "A file is uploading for another lesson. Wait for it to finish."
            : "A recording is already running for another lesson. Stop it first."}
        </p>
      )}

      {recorder.error !== null && (
        <p className="text-xs text-red-600 dark:text-red-400">{recorder.error}</p>
      )}

      <AnimatePresence mode="wait" initial={false}>
        {currentMode === "capturing" && (
          <motion.div key="capturing" {...enter} className="flex flex-col gap-2">
            <div className="flex items-center gap-2 font-mono text-sm text-[#EF4444]">
              <CircleDot className="h-3.5 w-3.5 animate-pulse" />
              {formatElapsed(recorder.elapsedMs)}
              <span className="text-neutral-500">
                · {recorder.pending} chunk{recorder.pending === 1 ? "" : "s"} pending
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={busy}
                className={primaryButtonClass}
                onClick={() => void guard(() => recorder.stop())}
              >
                <span className="flex items-center gap-1.5">
                  <Square className="h-3 w-3 fill-current" />
                  Stop
                </span>
              </button>

              <button
                type="button"
                disabled={busy}
                className={secondaryButtonClass}
                onClick={() => void guard(() => recorder.cancel())}
              >
                Discard
              </button>
            </div>

            <p className="text-[11px] leading-relaxed text-neutral-500">
              Chunks are saved on this device first and deleted only once the server
              confirms them - closing the laptop or losing wifi will not lose audio.
            </p>
          </motion.div>
        )}

        {currentMode === "draining" && (
          <motion.div key="draining" {...enter} className="flex flex-col gap-2">
            <div className="flex items-center gap-2 font-mono text-xs text-neutral-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />
              {recorder.status === "finalizing"
                ? "Finalizing on the server…"
                : `Uploading - ${recorder.pending} chunk${recorder.pending === 1 ? "" : "s"} left`}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={busy}
                className={secondaryButtonClass}
                onClick={() => void guard(() => recorder.cancel())}
              >
                Discard
              </button>
            </div>

            <p className="text-[11px] leading-relaxed text-neutral-500">
              Chunks are saved on this device first and deleted only once the server
              confirms them - closing the laptop or losing wifi will not lose audio.
            </p>
          </motion.div>
        )}

        {currentMode === "extracting" && recorder.upload !== null && (
          <motion.div key="extracting" {...enter} className="flex flex-col gap-2.5">
            <div className="flex items-center gap-2.5">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-neutral-900/[0.05] dark:bg-white/[0.08]">
                {joining ? (
                  <Combine className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                ) : (
                  <FileVideo className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
                )}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span
                  className="truncate text-xs font-medium text-[#222] dark:text-[#ededeb]"
                  title={recorder.upload.fileName}
                >
                  {recorder.upload.fileName}
                </span>
                <span className="font-mono text-[10px] text-neutral-500">
                  {joining ? "Joining the parts in order…" : "Taking the audio out as MP3…"}
                </span>
              </span>
              <span className="font-mono text-xs font-medium tabular-nums text-[#222] dark:text-[#ededeb]">
                {Math.floor(recorder.upload.progress * 100)}%
              </span>
            </div>

            <ProgressBar value={recorder.upload.progress} active />

            <div className="flex items-center gap-2">
              <button
                type="button"
                className={secondaryButtonClass}
                onClick={() => void recorder.cancel()}
              >
                Cancel
              </button>
            </div>

            <p className="text-[11px] leading-relaxed text-neutral-500">
              {joining
                ? "The parts become one recording here, so only the joined audio is uploaded."
                : "Only the soundtrack is uploaded - the video never leaves this device."}
            </p>
          </motion.div>
        )}

        {currentMode === "uploading-file" && recorder.upload !== null && (
          <motion.div key="uploading-file" {...enter} className="flex flex-col gap-2.5">
            <div className="flex items-center gap-2.5">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-neutral-900/[0.05] dark:bg-white/[0.08]">
                <FileAudio className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span
                  className="truncate text-xs font-medium text-[#222] dark:text-[#ededeb]"
                  title={recorder.upload.fileName}
                >
                  {recorder.upload.fileName}
                </span>
                <span className="flex items-center gap-1 font-mono text-[10px] text-neutral-500">
                  {recorder.upload.part !== null && (
                    <span className="tabular-nums">
                      part {recorder.upload.part.index + 1} of {recorder.upload.part.count} ·
                    </span>
                  )}
                  {finalizing ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Finalizing on the server…
                    </>
                  ) : (
                    `${formatBytes(recorder.upload.totalBytes * progress)} of ${formatBytes(recorder.upload.totalBytes)}`
                  )}
                </span>
              </span>
              <span className="font-mono text-xs font-medium tabular-nums text-[#222] dark:text-[#ededeb]">
                {Math.floor(progress * 100)}%
              </span>
            </div>

            <ProgressBar value={progress} active={!finalizing} />

            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={busy || finalizing}
                className={secondaryButtonClass}
                onClick={() => void guard(() => recorder.cancel())}
              >
                Cancel upload
              </button>
            </div>

            <p className="text-[11px] leading-relaxed text-neutral-500">
              Sent straight from the file - nothing is copied on this device. If the page
              closes, pick the same file again to pick up where it stopped.
            </p>
          </motion.div>
        )}

        {(currentMode === "stored" || currentMode === "uploaded") && (
          <motion.div
            key={currentMode}
            {...enter}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="flex flex-col gap-2"
          >
            <div className="flex min-w-0 items-center gap-2 font-mono text-sm text-[#22C55E]">
              <motion.span
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 22 }}
                className="flex shrink-0 items-center"
              >
                <Check className="h-3.5 w-3.5 stroke-[2.5]" />
              </motion.span>
              {currentMode === "uploaded" && recorder.upload !== null ? (
                <>
                  <span
                    className="min-w-0 truncate text-xs text-[#222] dark:text-[#ededeb]"
                    title={recorder.upload.fileName}
                  >
                    {recorder.upload.fileName}
                  </span>
                  {recorder.elapsedMs > 0 && (
                    <span className="shrink-0 text-xs text-neutral-500">
                      · {formatElapsed(recorder.elapsedMs)}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span>{formatElapsed(recorder.elapsedMs)}</span>
                  <span className="text-neutral-500">· audio stored</span>
                </>
              )}
              {recorder.partial && (
                <span className="flex items-center gap-1 text-xs text-[#F59E0B]">
                  · <TriangleAlert className="h-3 w-3" /> gaps detected
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {currentMode === "uploaded" ? (
                <button
                  type="button"
                  disabled={busy}
                  className={secondaryButtonClass}
                  onClick={() => fileRef.current?.click()}
                >
                  <span className="flex items-center gap-1.5">
                    <Upload className="h-3 w-3 text-neutral-400" />
                    Upload another
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  className={secondaryButtonClass}
                  onClick={record}
                >
                  <span className="flex items-center gap-1.5">
                    <RotateCcw className="h-3 w-3 text-neutral-400" />
                    Record again
                  </span>
                </button>
              )}

              <button
                type="button"
                disabled={busy}
                onClick={() => recorder.reset()}
                className="cursor-pointer font-mono text-[11px] text-neutral-500 transition-colors hover:text-neutral-800 dark:hover:text-neutral-200"
              >
                Reset
              </button>
            </div>

            <p className="text-[11px] leading-relaxed text-neutral-500">
              {currentMode === "uploaded"
                ? "Stored and linked to this lesson. Transcription starts on its own, and the server keeps a compact speech copy rather than the original file."
                : "Audio is stored on the server and linked to this lesson - closing the laptop or navigating away will not lose it."}
            </p>
          </motion.div>
        )}

        {currentMode === "idle" && (
          <motion.div key="idle" {...enter} className="flex flex-col gap-2.5">
            <div className="flex items-center gap-2">
              <RecordSplitButton
                size="md"
                align="start"
                disabled={busy || elsewhere}
                starting={starting}
                onRecord={record}
                onFiles={uploadFiles}
              />
            </div>

            <button
              type="button"
              disabled={busy || elsewhere || starting}
              onClick={() => fileRef.current?.click()}
              className="group flex w-full cursor-pointer items-center gap-3 rounded-lg border border-dashed border-[#d1d1cf] px-3 py-2.5 text-left transition-colors hover:border-neutral-400 hover:bg-neutral-900/[0.02] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-[#d1d1cf] disabled:hover:bg-transparent dark:border-[#2a2a28] dark:hover:border-neutral-600 dark:hover:bg-white/[0.03] dark:disabled:hover:border-[#2a2a28]"
            >
              <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-neutral-900/[0.05] transition-transform duration-200 ease-out group-hover:-translate-y-0.5 group-disabled:translate-y-0 dark:bg-white/[0.08]">
                <Upload className="h-3.5 w-3.5 text-neutral-500 dark:text-neutral-400" />
              </span>
              <span className="flex min-w-0 flex-col">
                <span className="text-xs font-medium text-[#222] dark:text-[#ededeb]">
                  Drop audio files, or browse
                </span>
                <span className="font-mono text-[10px] text-neutral-500">
                  mp3, m4a, wav, mp4 video… · up to {formatBytes(MAX_UPLOAD_BYTES)}
                </span>
              </span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <input
        ref={fileRef}
        type="file"
        accept={AUDIO_ACCEPT}
        multiple
        className="hidden"
        tabIndex={-1}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          event.target.value = "";
          if (files.length > 0) uploadFiles(files);
        }}
      />
    </motion.div>
  );
}
